import { useState, useEffect, useCallback } from 'react';
// TEACHING_SCHEDULE_ASSIGNMENT_FIXES_20260916
import RichTextEditor from '../../../components/RichTextEditor';
import {
  buildAttendanceContributionTableHtml,
  normalizeAttendanceSettings
} from '../../../utils/attendanceCalculations';
import {
  downloadSyllabusPdfRoot
} from '../../../utils/syllabusPdf';
import {
  expandInstructorAssignmentScheduleOccurrences,
  loadStudentAssignmentScheduleContext
} from '../../../utils/studentAssignmentSchedule';

// PRINT_TEACHING_SCHEDULE_EVENTS_20260916

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const HEADERS = {
  'apikey': SUPABASE_KEY,
  'Authorization': 'Bearer ' + SUPABASE_KEY
};

const JSON_HEADERS = {
  ...HEADERS,
  'Content-Type': 'application/json'
};


const SYSTEM_ASSIGNMENT_TYPE_NAMES = new Set([
  'Attendance',
  'Critical Questions',
  'Questions Assignment',
  'Philosophical Diary',
  'Question Improvement'
]);

function isOrdinaryAssignmentType(type) {
  if (!type) return false;

  return (
    type.is_core !== true &&
    !SYSTEM_ASSIGNMENT_TYPE_NAMES.has(type.name)
  );
}

function normalizeQuizQuestions(value) {
  let rows = value;

  if (typeof rows === 'string') {
    try {
      rows = JSON.parse(rows);
    } catch {
      rows = [];
    }
  }

  if (!Array.isArray(rows)) return [];

  return [...rows].sort(
    (a, b) =>
      (Number(a.question_order) || 0) -
      (Number(b.question_order) || 0)
  );
}

function parseQuizChoices(value) {
  if (Array.isArray(value)) {
    return value.map(choice => String(choice));
  }

  if (typeof value !== 'string') return [];

  try {
    const parsed = JSON.parse(value);

    if (Array.isArray(parsed)) {
      return parsed.map(choice => String(choice));
    }
  } catch {
    // Fall through to line-separated text.
  }

  return value
    .split('\n')
    .map(choice => choice.trim())
    .filter(Boolean);
}

function makeQuizDraft(value) {
  return normalizeQuizQuestions(value).map(question => ({
    question: question.question || '',
    choicesText: parseQuizChoices(question.choices).join('\n'),
    correct_answer_index:
      question.correct_answer_index === null ||
      question.correct_answer_index === undefined ||
      question.correct_answer_index === ''
        ? ''
        : Number(question.correct_answer_index)
  }));
}

function serializeQuizDraft(value) {
  const rows = Array.isArray(value) ? value : [];

  return rows.map((question, index) => {
    const prompt = String(question.question || '').trim();

    const choices = String(question.choicesText || '')
      .split('\n')
      .map(choice => choice.trim())
      .filter(Boolean);

    if (!prompt) {
      throw new Error(
        `Quiz question ${index + 1} needs a question.`
      );
    }

    if (choices.length < 2) {
      throw new Error(
        `Quiz question ${index + 1} needs at least two answer choices.`
      );
    }

    if (
      question.correct_answer_index === '' ||
      question.correct_answer_index === null ||
      question.correct_answer_index === undefined
    ) {
      throw new Error(
        `Quiz question ${index + 1} needs a correct answer.`
      );
    }

    const correctIndex =
      Number(question.correct_answer_index);

    if (
      !Number.isInteger(correctIndex) ||
      correctIndex < 0 ||
      correctIndex >= choices.length
    ) {
      throw new Error(
        `Quiz question ${index + 1} has an invalid correct answer.`
      );
    }

    return {
      question: prompt,
      choices: JSON.stringify(choices),
      correct_answer_index: correctIndex,
      correct_answer: choices[correctIndex],
      question_order: index + 1
    };
  });
}

function isCriticalQuestionsSection(section) {
  if (!section) return false;

  return (
    section.name === 'Critical Questions' ||
    section.name === 'Questions Assignment' ||
    section.has_clarificatory_variants === true
  );
}

function isSectionApplicable(section, course) {
  if (!section) return false;

  if (section.name === 'Attendance') {
    return course.attendance_enabled === true;
  }

  if (isCriticalQuestionsSection(section)) {
    return (
      course.cq_enabled === true ||
      course.qi_enabled === true
    );
  }

  if (section.name === 'Philosophical Diary') {
    return course.pd_enabled === true;
  }

  if (section.name === 'Question Improvement') {
    return course.qi_enabled === true;
  }

  return true;
}

// Fetch sections active for this course, ordered
async function fetchCourseSections(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_sections?course_id=eq.${courseId}&select=id,section_order,cooldown_minutes_override,quiz_questions_override,syllabus_sections(*,syllabus_quiz_questions(*))&order=section_order.asc`,
    { headers: HEADERS }
  );

  return response.json();
}

// Fetch all sections available to this instructor, including system-wide sections.
async function fetchLibrarySections(
  instructorId,
  courseId
) {
  const ownershipFilter = instructorId
    ? `or=(instructor_id.is.null,instructor_id.eq.${instructorId})`
    : 'instructor_id=is.null';

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_sections?${ownershipFilter}&select=*,syllabus_quiz_questions(*)`,
    { headers: HEADERS }
  );

  const data = await response.json();

  if (!Array.isArray(data)) return [];

  const visibleSections =
    data.filter(
      section =>
        !section.course_scope_id ||
        String(section.course_scope_id) ===
          String(courseId)
    );

  // Use each section's intended library order. For sections sharing the same
  // order value, preserve creation order when created_at is available.
  return [...visibleSections].sort((a, b) => {
    const aOrder = Number(a.section_order);
    const bOrder = Number(b.section_order);
    const safeA = Number.isFinite(aOrder) ? aOrder : Number.MAX_SAFE_INTEGER;
    const safeB = Number.isFinite(bOrder) ? bOrder : Number.MAX_SAFE_INTEGER;

    if (safeA !== safeB) return safeA - safeB;

    const aCreated = String(a.created_at || '');
    const bCreated = String(b.created_at || '');

    if (aCreated && bCreated && aCreated !== bCreated) {
      return aCreated.localeCompare(bCreated);
    }

    return 0;
  });
}

async function fetchCourseContent(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_content?course_id=eq.${courseId}&select=*`,
    { headers: HEADERS }
  );

  return response.json();
}

async function fetchCourseAssignments(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignments?course_id=eq.${courseId}&select=*,assignment_types(id,name,is_core,requires_submission,instructions,syllabus_cooldown_minutes,assignment_type_quiz_questions(*))&order=instance_number.asc`,
    { headers: HEADERS }
  );

  return response.json();
}


async function fetchPrintWeeks(courseId) {
  return fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${courseId}&select=*,course_week_content(description)&order=week_number.asc`,
    { headers: HEADERS }
  ).then(r => r.json());
}

async function fetchPrintMeetings(courseId) {
  return fetch(
    `${SUPABASE_URL}/rest/v1/student_meetings?course_id=eq.${courseId}&select=*&order=meeting_date.asc`,
    { headers: HEADERS }
  ).then(r => r.json());
}

async function fetchPrintReadings(courseId) {
  return fetch(
    `${SUPABASE_URL}/rest/v1/course_week_reading_assignments?select=*,course_readings(*),course_weeks!inner(course_id)&course_weeks.course_id=eq.${courseId}&order=display_order.asc`,
    { headers: HEADERS }
  ).then(r => r.json());
}

async function fetchPrintEvents(courseId) {
  return fetch(
    `${SUPABASE_URL}/rest/v1/course_events?select=*,course_weeks!inner(course_id)&course_weeks.course_id=eq.${courseId}&order=event_date.asc,event_time.asc`,
    { headers: HEADERS }
  ).then(r => r.json());
}

async function fetchPrintAssignmentTypeSettings(
  courseId
) {
  return fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=course_id,assignment_type_id,display_name,schedule_enabled,schedule_choice_enabled,schedule_open_offset_days,schedule_due_offset_days`,
    { headers: HEADERS }
  ).then(r => r.json());
}

async function fetchPrintAiPermissions(courseId) {
  return fetch(
    `${SUPABASE_URL}/rest/v1/course_ai_permissions?course_id=eq.${courseId}&select=*,ai_use_types(label,description,ai_use_categories(name,display_order))`,
    { headers: HEADERS }
  ).then(r => r.json());
}

async function fetchPrintHolidays(instructorId) {
  if (!instructorId) return [];

  const instructorRows = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=eq.${instructorId}&select=institution_id&limit=1`,
    { headers: HEADERS }
  ).then(r => r.json());

  const institutionId = instructorRows?.[0]?.institution_id;
  if (!institutionId) return [];

  return fetch(
    `${SUPABASE_URL}/rest/v1/holidays?institution_id=eq.${institutionId}&or=(is_personal.eq.false,instructor_id.eq.${instructorId})&select=*&order=date.asc`,
    { headers: HEADERS }
  ).then(r => r.json());
}

async function fetchPrintAttendanceWeight(courseId) {
  const types = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_types?name=eq.Attendance&select=id&limit=1`,
    { headers: HEADERS }
  ).then(r => r.json());

  const typeId = types?.[0]?.id;
  if (!typeId) return 0;

  const rows = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_weights?course_id=eq.${courseId}&assignment_type_id=eq.${typeId}&select=weight_percent&limit=1`,
    { headers: HEADERS }
  ).then(r => r.json());

  return Number(rows?.[0]?.weight_percent) || 0;
}

async function fetchPrintAttendanceGroupWeeks(courseId) {
  return fetch(
    `${SUPABASE_URL}/rest/v1/attendance_group_weeks?select=attendance_group_id,course_week_id,attendance_groups!inner(course_id)&attendance_groups.course_id=eq.${courseId}`,
    { headers: HEADERS }
  ).then(r => r.json());
}

function escapePrintHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function safePrintFilePart(value) {
  return String(value || '')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatPrintDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleDateString('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

function stripPrintHtml(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

function substitutePrintVariables(content, course, attendanceTableHtml) {
  if (!content) return '';

  let result = content
    .replace(/\{\{course_name\}\}/g, course.course_titles?.name || course.name || '')
    .replace(/\{\{semester\}\}/g, course.semesters?.name || '')
    .replace(/\{\{class_day\}\}/g, course.class_day || '')
    .replace(/\{\{class_time\}\}/g, (() => {
      const times = course.class_times;
      const days = course.class_days;
      if (times && days?.length) {
        return days
          .map(day => times[day] ? times[day].slice(0, 5) : '')
          .filter(Boolean)
          .join(' / ');
      }
      return course.class_time?.slice(0, 5) || '';
    })())
    .replace(/\{\{pd_due_day\}\}/g, course.pd_due_day || '')
    .replace(/\{\{start_date\}\}/g, course.semesters?.start_date || '')
    .replace(/\{\{mid_eval_close\}\}/g, course.mid_eval_close_date || '')
    .replace(/\{\{final_eval_close\}\}/g, course.final_eval_close_date || '')
    .replace(/\{\{cq_clarificatory_allowed\}\}/g, course.cq_clarificatory_allowed?.toString() || '0');

  return result
    .replace(
      /<p>\s*\{\{attendance_calculation_table\}\}\s*<\/p>/gi,
      attendanceTableHtml
    )
    .replace(
      /\{\{attendance_calculation_table\}\}/g,
      attendanceTableHtml
    );
}


function getPrintCalendarWeekStart(
  dateStr
) {
  if (!dateStr) return '';

  const parts = String(dateStr)
    .slice(0, 10)
    .split('-')
    .map(Number);

  if (
    parts.length !== 3 ||
    !parts.every(Number.isFinite)
  ) {
    return '';
  }

  const date = new Date(
    parts[0],
    parts[1] - 1,
    parts[2],
    12,
    0,
    0
  );

  const dayOfWeek =
    date.getDay();

  date.setDate(
    date.getDate() -
      (
        dayOfWeek === 0
          ? 6
          : dayOfWeek - 1
      )
  );

  return [
    date.getFullYear(),
    String(
      date.getMonth() + 1
    ).padStart(2, '0'),
    String(
      date.getDate()
    ).padStart(2, '0')
  ].join('-');
}


function isFirstActualPrintClassOfWeek(
  week,
  weeks,
  holidays
) {
  const weekStart =
    getPrintCalendarWeekStart(
      week.class_date
    );

  const sameWeek = weeks
    .filter(
      candidate =>
        getPrintCalendarWeekStart(
          candidate.class_date
        ) === weekStart
    )
    .slice()
    .sort(
      (a, b) =>
        String(
          a.class_date || ''
        ).localeCompare(
          String(
            b.class_date || ''
          )
        )
    );

  const firstActualClass =
    sameWeek.find(
      candidate =>
        !holidays.some(
          holiday =>
            holiday.date ===
            candidate.class_date
        )
    ) ||
    sameWeek[0];

  if (!firstActualClass) {
    return false;
  }

  if (
    firstActualClass.id &&
    week.id
  ) {
    return (
      firstActualClass.id ===
      week.id
    );
  }

  return (
    firstActualClass.class_date ===
    week.class_date
  );
}

async function fetchTypeDescriptions(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?course_id=eq.${courseId}&select=*`,
    { headers: HEADERS }
  );

  return response.json();
}

async function fetchTypeDisplayNames(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_settings?course_id=eq.${courseId}&select=assignment_type_id,display_name`,
    { headers: HEADERS }
  );

  return response.json();
}

async function upsertCourseContent(courseId, sectionId, content) {
  const existing = await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_content?course_id=eq.${courseId}&syllabus_section_id=eq.${sectionId}&select=id`,
    { headers: HEADERS }
  ).then(r => r.json());

  if (existing?.[0]?.id) {
    await fetch(
      `${SUPABASE_URL}/rest/v1/course_syllabus_content?id=eq.${existing[0].id}`,
      {
        method: 'PATCH',
        headers: JSON_HEADERS,
        body: JSON.stringify({ content, updated_at: new Date().toISOString() })
      }
    );
  } else {
    await fetch(
      `${SUPABASE_URL}/rest/v1/course_syllabus_content`,
      {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ course_id: courseId, syllabus_section_id: sectionId, content, updated_at: new Date().toISOString() })
      }
    );
  }
}

async function deleteCourseContent(
  courseId,
  sectionId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_content?course_id=eq.${courseId}&syllabus_section_id=eq.${sectionId}`,
    {
      method: 'DELETE',
      headers: HEADERS
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Resetting course syllabus content failed (${response.status}): ${detail}`
    );
  }
}

async function updateCourseSectionOverrides(
  courseSyllabusSectionId,
  cooldownMinutes,
  quizQuestions
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_sections?id=eq.${courseSyllabusSectionId}`,
    {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        cooldown_minutes_override:
          cooldownMinutes,
        quiz_questions_override:
          quizQuestions
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Saving course syllabus overrides failed (${response.status}): ${detail}`
    );
  }
}

async function resetTypeSettings(
  courseId,
  assignmentTypeId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?course_id=eq.${courseId}&assignment_type_id=eq.${assignmentTypeId}`,
    {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        description: '',
        syllabus_cooldown_minutes: null,
        syllabus_quiz_questions: null,
        updated_at: new Date().toISOString()
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Resetting assignment syllabus settings failed (${response.status}): ${detail}`
    );
  }
}

async function upsertTypeSettings(
  courseId,
  assignmentTypeId,
  {
    description,
    syllabusCooldownMinutes,
    syllabusQuizQuestions
  }
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?on_conflict=course_id,assignment_type_id`,
    {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        'Prefer': 'resolution=merge-duplicates'
      },
      body: JSON.stringify({
        course_id: courseId,
        assignment_type_id: assignmentTypeId,
        description,
        syllabus_cooldown_minutes:
          syllabusCooldownMinutes,
        syllabus_quiz_questions:
          syllabusQuizQuestions,
        updated_at: new Date().toISOString()
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Saving assignment syllabus settings failed (${response.status}): ${detail}`
    );
  }
}


async function upsertTypeOrder(
  courseId,
  assignmentTypeId,
  description,
  sectionOrder
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?on_conflict=course_id,assignment_type_id`,
    {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        'Prefer': 'resolution=merge-duplicates'
      },
      body: JSON.stringify({
        course_id: courseId,
        assignment_type_id: assignmentTypeId,
        description: description ?? '',
        section_order: sectionOrder,
        updated_at: new Date().toISOString()
      })
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Saving assignment syllabus order failed (${response.status}): ${detail}`
    );
  }
}

async function removeTypeDescription(
  courseId,
  assignmentTypeId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_assignment_type_descriptions?course_id=eq.${courseId}&assignment_type_id=eq.${assignmentTypeId}`,
    {
      method: 'DELETE',
      headers: HEADERS
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Removing assignment syllabus section failed (${response.status}): ${detail}`
    );
  }
}

async function addSectionToCourse(
  courseId,
  sectionId,
  sectionOrder
) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_sections`,
    {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        'Prefer': 'resolution=merge-duplicates'
      },
      body: JSON.stringify({
        course_id: courseId,
        syllabus_section_id: sectionId,
        section_order: sectionOrder
      })
    }
  );
}

async function removeSectionFromCourse(
  courseSyllabusSectionId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_sections?id=eq.${courseSyllabusSectionId}`,
    {
      method: 'DELETE',
      headers: HEADERS
    }
  );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      `Removing syllabus section failed (${response.status}): ${detail}`
    );
  }
}

async function updateSectionOrder(
  courseSyllabusSectionId,
  sectionOrder
) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/course_syllabus_sections?id=eq.${courseSyllabusSectionId}`,
    {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        section_order: sectionOrder
      })
    }
  );
}

async function createSection(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/syllabus_sections`,
    {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  return response.json();
}

const VARIABLES = [
  {
    label: 'Course name',
    placeholder: '{{course_name}}'
  },
  {
    label: 'Semester',
    placeholder: '{{semester}}'
  },
  {
    label: 'Class day',
    placeholder: '{{class_day}}'
  },
  {
    label: 'Class time',
    placeholder: '{{class_time}}'
  },
  {
    label: 'PD due day',
    placeholder: '{{pd_due_day}}'
  },
  {
    label: 'Semester start date',
    placeholder: '{{start_date}}'
  },
  {
    label: 'Mid-Semester Self-Evaluation due date',
    placeholder: '{{mid_eval_close}}'
  },
  {
    label: 'Final Self-Evaluation due date',
    placeholder: '{{final_eval_close}}'
  },
  {
    label: 'Clarificatory questions allowed',
    placeholder: '{{cq_clarificatory_allowed}}'
  },
  {
    label: 'Attendance calculation table',
    placeholder: '{{attendance_calculation_table}}'
  }
];

function VariablePicker({ onInsert }) {
  const [selected, setSelected] = useState('');

  return (
    <div style={styles.variablePicker}>
      <label style={styles.varLabel}>
        Insert variable:
      </label>

      <div style={styles.varRow}>
        <select
          style={styles.varSelect}
          value={selected}
          onChange={e =>
            setSelected(e.target.value)
          }
        >
          <option value="">
            Select a variable
          </option>

          {VARIABLES.map(v => (
            <option
              key={v.placeholder}
              value={v.placeholder}
            >
              {v.label}
            </option>
          ))}
        </select>

        <button
          type="button"
          style={styles.varInsertBtn}
          disabled={!selected}
          onClick={() => {
            onInsert(selected);
            setSelected('');
          }}
        >
          Insert
        </button>
      </div>
    </div>
  );
}

function CourseOverrideSettings({
  sourceLabel,
  draft,
  onCooldownChange,
  onQuizQuestionChange,
  onAddQuizQuestion,
  onRemoveQuizQuestion
}) {
  const questions =
    draft?.quizQuestions || [];

  return (
    <div
      style={{
        marginTop: '18px',
        paddingTop: '16px',
        borderTop:
          '1px solid #d7dee5'
      }}
    >
      <div
        style={{
          marginBottom: '18px'
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '7px',
            flexWrap: 'wrap'
          }}
        >
          <strong>
            Cooldown
          </strong>

          <span
            style={{
              fontSize: '12px',
              color:
                draft?.cooldownIsOverride
                  ? '#8a5b00'
                  : '#66788a'
            }}
          >
            {draft?.cooldownIsOverride
              ? 'Course override'
              : `Using ${sourceLabel} default`}
          </span>
        </div>

        <input
          type="number"
          min="0"
          step="0.1"
          value={
            draft?.cooldownMinutes ??
            0
          }
          onChange={e =>
            onCooldownChange(
              e.target.value
            )
          }
          style={{
            width: '100px',
            border:
              '1px solid #d7dee5',
            borderRadius: '8px',
            padding: '7px 10px',
            fontSize: '14px'
          }}
        />
        {' '}minutes
      </div>

      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginBottom: '10px',
            flexWrap: 'wrap'
          }}
        >
          <strong>
            Quiz
          </strong>

          <span
            style={{
              fontSize: '12px',
              color:
                draft?.quizIsOverride
                  ? '#8a5b00'
                  : '#66788a'
            }}
          >
            {draft?.quizIsOverride
              ? 'Course override'
              : `Using ${sourceLabel} default`}
          </span>
        </div>

        {questions.length === 0 && (
          <div
            style={{
              fontSize: '13px',
              color: '#66788a',
              marginBottom: '10px'
            }}
          >
            No quiz questions.
          </div>
        )}

        {questions.map(
          (question, index) => {
            const choices =
              String(
                question.choicesText ||
                ''
              )
                .split('\n')
                .map(choice =>
                  choice.trim()
                )
                .filter(Boolean);

            return (
              <div
                key={index}
                style={{
                  border:
                    '1px solid #d7dee5',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '10px',
                  background:
                    '#f7f9fa'
                }}
              >
                <div
                  style={{
                    marginBottom: '10px'
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      fontWeight: '700',
                      fontSize: '13px',
                      marginBottom: '5px'
                    }}
                  >
                    Question {index + 1}
                  </label>

                  <input
                    type="text"
                    value={
                      question.question ||
                      ''
                    }
                    onChange={e =>
                      onQuizQuestionChange(
                        index,
                        {
                          question:
                            e.target.value
                        }
                      )
                    }
                    style={{
                      width: '100%',
                      boxSizing:
                        'border-box',
                      border:
                        '1px solid #d7dee5',
                      borderRadius: '8px',
                      padding: '8px 10px'
                    }}
                  />
                </div>

                <div
                  style={{
                    marginBottom: '10px'
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      fontWeight: '700',
                      fontSize: '13px',
                      marginBottom: '5px'
                    }}
                  >
                    Answer choices
                    (one per line)
                  </label>

                  <textarea
                    rows={4}
                    value={
                      question.choicesText ||
                      ''
                    }
                    onChange={e =>
                      onQuizQuestionChange(
                        index,
                        {
                          choicesText:
                            e.target.value
                        }
                      )
                    }
                    style={{
                      width: '100%',
                      boxSizing:
                        'border-box',
                      border:
                        '1px solid #d7dee5',
                      borderRadius: '8px',
                      padding: '8px 10px',
                      fontFamily:
                        'inherit'
                    }}
                  />
                </div>

                <div
                  style={{
                    marginBottom: '10px'
                  }}
                >
                  <label
                    style={{
                      display: 'block',
                      fontWeight: '700',
                      fontSize: '13px',
                      marginBottom: '5px'
                    }}
                  >
                    Correct answer
                  </label>

                  <select
                    value={
                      question.correct_answer_index ??
                      ''
                    }
                    onChange={e =>
                      onQuizQuestionChange(
                        index,
                        {
                          correct_answer_index:
                            e.target.value ===
                            ''
                              ? ''
                              : Number(
                                  e
                                    .target
                                    .value
                                )
                        }
                      )
                    }
                    style={{
                      border:
                        '1px solid #d7dee5',
                      borderRadius: '8px',
                      padding: '7px 10px'
                    }}
                  >
                    <option value="">
                      Select correct answer...
                    </option>

                    {choices.map(
                      (
                        choice,
                        choiceIndex
                      ) => (
                        <option
                          key={
                            choiceIndex
                          }
                          value={
                            choiceIndex
                          }
                        >
                          {choice}
                        </option>
                      )
                    )}
                  </select>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    onRemoveQuizQuestion(
                      index
                    )
                  }
                  style={{
                    background:
                      'transparent',
                    border:
                      '1px solid #e2b8b8',
                    color: '#a12a2a',
                    borderRadius: '6px',
                    padding: '5px 9px',
                    cursor: 'pointer'
                  }}
                >
                  Remove Question
                </button>
              </div>
            );
          }
        )}

        <button
          type="button"
          onClick={
            onAddQuizQuestion
          }
          style={{
            background:
              'transparent',
            border:
              '1px solid #b0cfe0',
            color: '#215b7d',
            borderRadius: '8px',
            padding: '7px 12px',
            cursor: 'pointer'
          }}
        >
          + Add Quiz Question
        </button>
      </div>
    </div>
  );
}

export default function CourseSyllabus({
  course,
  session
}) {
  const [courseSections, setCourseSections] =
    useState([]);

  const [librarySections, setLibrarySections] =
    useState([]);

  const [courseContent, setCourseContent] =
    useState({});

  const [
    courseAssignments,
    setCourseAssignments
  ] = useState([]);

  const [
    typeDescriptions,
    setTypeDescriptions
  ] = useState({});

  const [
    typeDisplayNames,
    setTypeDisplayNames
  ] = useState({});

  const [
    typeOrders,
    setTypeOrders
  ] = useState({});

  const [
  typeCooldownOverrides,
  setTypeCooldownOverrides
] = useState({});

const [
  typeQuizOverrides,
  setTypeQuizOverrides
] = useState({});

const [
  assignmentEdits,
  setAssignmentEdits
] = useState({});

const [
  sectionEdits,
  setSectionEdits
] = useState({});

  const [loading, setLoading] =
    useState(true);

  const [expandedId, setExpandedId] =
    useState(null);

  const [editValues, setEditValues] =
    useState({});

  const [saving, setSaving] =
    useState(false);

  const [printing, setPrinting] =
    useState(false);

  const [error, setError] =
    useState('');

  const [success, setSuccess] =
    useState('');

  const [
    showAddPanel,
    setShowAddPanel
  ] = useState(false);

  const [
    showNewSectionForm,
    setShowNewSectionForm
  ] = useState(false);

  const [
    newSectionName,
    setNewSectionName
  ] = useState('');

  const instructorId =
    course?.instructor_id ||
    session?.instructorId;

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const [
        cs,
        lib,
        cc,
        ca,
        td,
        tdn
      ] = await Promise.all([
        fetchCourseSections(course.id),
        fetchLibrarySections(
          instructorId,
          course.id
        ),
        fetchCourseContent(course.id),
        fetchCourseAssignments(course.id),
        fetchTypeDescriptions(course.id),
        fetchTypeDisplayNames(course.id)
      ]);

      const library =
        Array.isArray(lib) ? lib : [];

      const initialCourseSections =
  Array.isArray(cs) ? cs : [];

setCourseSections(
  initialCourseSections
);

      setLibrarySections(library);

      const contentMap = {};

      (
        Array.isArray(cc)
          ? cc
          : []
      ).forEach(c => {
        contentMap[
          c.syllabus_section_id
        ] = c.content;
      });

      setCourseContent(contentMap);

      setCourseAssignments(
        Array.isArray(ca) ? ca : []
      );

      const descMap = {};
      const orderMap = {};
      const cooldownMap = {};
      const quizMap = {};

      (
        Array.isArray(td)
          ? td
          : []
      ).forEach(d => {
        const typeId =
          d.assignment_type_id;

        descMap[typeId] =
          d.description;

        if (
          d.section_order !== null &&
          d.section_order !== undefined
        ) {
          orderMap[typeId] =
            Number(d.section_order);
        }

        cooldownMap[typeId] =
          d.syllabus_cooldown_minutes === null ||
          d.syllabus_cooldown_minutes === undefined
            ? null
            : Number(
                d.syllabus_cooldown_minutes
              );

        quizMap[typeId] =
          d.syllabus_quiz_questions === null ||
          d.syllabus_quiz_questions === undefined
            ? null
            : normalizeQuizQuestions(
                d.syllabus_quiz_questions
              );
      });

      setTypeDescriptions(descMap);

      const displayNameMap = {};

      (
        Array.isArray(tdn)
          ? tdn
          : []
      ).forEach(row => {
        const value =
          String(
            row.display_name || ''
          ).trim();

        if (value) {
          displayNameMap[
            row.assignment_type_id
          ] = value;
        }
      });

      setTypeDisplayNames(
        displayNameMap
      );

      setTypeOrders(orderMap);
      setTypeCooldownOverrides(
        cooldownMap
      );
      setTypeQuizOverrides(
        quizMap
      );
    } finally {
      setLoading(false);
    }
  }, [
    course.id,
    instructorId
  ]);

  useEffect(() => {
    load();
  }, [load]);

  // Sections currently active in this course
const activeSectionIds = new Set(
  courseSections
    .map(
      cs =>
        cs.syllabus_sections?.id
    )
    .filter(Boolean)
);

const availableToAdd =
  librarySections.filter(
    section =>
      !activeSectionIds.has(section.id) &&
      section.name !== 'Other Assignments' &&
      isSectionApplicable(
        section,
        course
      )
  );

function getUniqueOrdinaryAssignments() {
  const seenTypes = new Set();
  const result = [];

  for (const assignment of courseAssignments) {
    if (
      !isOrdinaryAssignmentType(
        assignment.assignment_types
      )
    ) {
      continue;
    }

    const typeId =
      assignment.assignment_type_id;

    if (seenTypes.has(typeId)) {
      continue;
    }

    seenTypes.add(typeId);
    result.push(assignment);
  }

  return result;
}

const availableAssignmentTypes =
  getUniqueOrdinaryAssignments()
    .filter(
      assignment =>
        !Object.prototype.hasOwnProperty.call(
          typeDescriptions,
          assignment.assignment_type_id
        )
    );

  function getOrderableEntries() {
  const result = [];

  const assignmentTypes =
    getUniqueOrdinaryAssignments()
      .filter(
        assignment =>
          Object.prototype.hasOwnProperty.call(
            typeDescriptions,
            assignment.assignment_type_id
          )
      );

  let pdOrder = null;

  for (const cs of courseSections) {
    const section =
      cs.syllabus_sections;

    if (!section) continue;

    if (
      section.name ===
      'Other Assignments'
    ) {
      continue;
    }

    const sectionOrder =
      Number(cs.section_order);

    const defaultCooldownMinutes =
      Number(
        section.cooldown_minutes ??
        0
      );

    const defaultQuizQuestions =
      normalizeQuizQuestions(
        section.syllabus_quiz_questions
      );

    const cooldownOverride =
      cs.cooldown_minutes_override;

    const quizOverride =
      cs.quiz_questions_override;

    result.push({
      ...section,

      section_order:
        Number.isFinite(sectionOrder)
          ? sectionOrder
          : 0,

      _cssId: cs.id,
      _cssOrder:
        cs.section_order,

      _orderKey:
        `section-${cs.id}`,

      _orderKind: 'section',

      isAssignment: false,
      isTypeDesc: false,

      defaultCooldownMinutes,
      defaultQuizQuestions,

      cooldownOverride,
      quizOverride,

      effectiveCooldownMinutes:
        cooldownOverride === null ||
        cooldownOverride === undefined
          ? defaultCooldownMinutes
          : Number(
              cooldownOverride
            ),

      effectiveQuizQuestions:
        quizOverride === null ||
        quizOverride === undefined
          ? defaultQuizQuestions
          : normalizeQuizQuestions(
              quizOverride
            )
    });

    if (
      section.name ===
      'Philosophical Diary'
    ) {
      pdOrder =
        Number.isFinite(sectionOrder)
          ? sectionOrder
          : 0;
    }
  }

  const fallbackBase =
    pdOrder !== null
      ? pdOrder + 0.5
      : result.reduce(
          (max, entry) =>
            Math.max(
              max,
              Number(
                entry.section_order
              ) || 0
            ),
          0
        ) + 10;

  assignmentTypes.forEach(
    (assignment, index) => {
      const typeId =
        assignment.assignment_type_id;

      const defaultCooldownMinutes =
        Number(
          assignment.assignment_types
            ?.syllabus_cooldown_minutes ??
          1
        );

      const defaultQuizQuestions =
        normalizeQuizQuestions(
          assignment.assignment_types
            ?.assignment_type_quiz_questions
        );

      const cooldownOverride =
        typeCooldownOverrides[
          typeId
        ];

      const quizOverride =
        typeQuizOverrides[
          typeId
        ];

      const storedOrder =
        Number(
          typeOrders[typeId]
        );

      result.push({
        id:
          `type-${typeId}`,

        name:
          typeDisplayNames[typeId] ||
          assignment.assignment_types
            ?.name ||
          'Assignment',

        section_order:
          Number.isFinite(
            storedOrder
          )
            ? storedOrder
            : fallbackBase +
              (index * 0.001),

        isAssignment: true,
        isTypeDesc: true,

        typeId,

        defaultContent:
          assignment.assignment_types
            ?.instructions ||
          '',

        defaultCooldownMinutes,
        defaultQuizQuestions,

        cooldownOverride,
        quizOverride,

        effectiveCooldownMinutes:
          cooldownOverride === null ||
          cooldownOverride === undefined
            ? defaultCooldownMinutes
            : Number(
                cooldownOverride
              ),

        effectiveQuizQuestions:
          quizOverride === null ||
          quizOverride === undefined
            ? defaultQuizQuestions
            : normalizeQuizQuestions(
                quizOverride
              ),

        _orderKey:
          `type-${typeId}`,

        _orderKind:
          'type'
      });
    }
  );

  return result.sort((a, b) => {
    const aOrder =
      Number(a.section_order);

    const bOrder =
      Number(b.section_order);

    const safeA =
      Number.isFinite(aOrder)
        ? aOrder
        : Number.MAX_SAFE_INTEGER;

    const safeB =
      Number.isFinite(bOrder)
        ? bOrder
        : Number.MAX_SAFE_INTEGER;

    if (safeA !== safeB) {
      return safeA - safeB;
    }

    return String(
      a._orderKey ||
      a.id ||
      ''
    ).localeCompare(
      String(
        b._orderKey ||
        b.id ||
        ''
      )
    );
  });
}

function getDisplaySections() {
  return getOrderableEntries()
    .filter(entry =>
      entry.isTypeDesc ||
      isSectionApplicable(
        entry,
        course
      )
    );
}


 function handleExpand(section) {
  if (
    expandedId === section.id
  ) {
    setExpandedId(null);
    return;
  }

  setExpandedId(section.id);

  if (section.isTypeDesc) {
    const cooldownOverride =
      typeCooldownOverrides[
        section.typeId
      ];

    const quizOverride =
      typeQuizOverrides[
        section.typeId
      ];

    const descriptionOverride =
      typeDescriptions[
        section.typeId
      ];

    setEditValues(prev => ({
      ...prev,
      [section.id]:
        descriptionOverride ||
        section.defaultContent ||
        ''
    }));

    setAssignmentEdits(prev => ({
      ...prev,
      [section.id]: {
        descriptionIsOverride:
          !!descriptionOverride,

        cooldownIsOverride:
          cooldownOverride !== null &&
          cooldownOverride !== undefined,

        cooldownMinutes:
          cooldownOverride === null ||
          cooldownOverride === undefined
            ? section.defaultCooldownMinutes
            : cooldownOverride,

        quizIsOverride:
          quizOverride !== null &&
          quizOverride !== undefined,

        quizQuestions:
          makeQuizDraft(
            quizOverride === null ||
            quizOverride === undefined
              ? section.defaultQuizQuestions
              : quizOverride
          )
      }
    }));
  } else {
    if (
      !section.is_teaching_schedule
    ) {
      setSectionEdits(prev => ({
        ...prev,
        [section.id]: {
          contentIsOverride:
            section.has_clarificatory_variants
              ? true
              : Object.prototype.hasOwnProperty.call(
                  courseContent,
                  section.id
                ),

          cooldownIsOverride:
            section.cooldownOverride !==
              null &&
            section.cooldownOverride !==
              undefined,

          cooldownMinutes:
            section.cooldownOverride ===
              null ||
            section.cooldownOverride ===
              undefined
              ? section.defaultCooldownMinutes
              : Number(
                  section.cooldownOverride
                ),

          quizIsOverride:
            section.quizOverride !==
              null &&
            section.quizOverride !==
              undefined,

          quizQuestions:
            makeQuizDraft(
              section.quizOverride ===
                null ||
              section.quizOverride ===
                undefined
                ? section.defaultQuizQuestions
                : section.quizOverride
            )
        }
      }));
    }

    if (
      section
        .has_clarificatory_variants
    ) {
      setEditValues(prev => ({
        ...prev,

        [`${section.id}_with`]:
          courseContent[
            `${section.id}_with`
          ] ??
          section
            .content_with_clarificatory ??
          '',

        [`${section.id}_without`]:
          courseContent[
            `${section.id}_without`
          ] ??
          section
            .content_without_clarificatory ??
          ''
      }));
    } else {
      setEditValues(prev => ({
        ...prev,

        [section.id]:
          courseContent[
            section.id
          ] ??
          section.content ??
          ''
      }));
    }
  }

  setError('');
  setSuccess('');
}

 async function handleResetToDefault(
  section
) {
  const sourceName =
    section.isTypeDesc
      ? 'Assignment Library'
      : 'General Syllabus';

  if (
    !window.confirm(
      `Reset "${section.name}" to the ${sourceName} defaults for this course?`
    )
  ) {
    return;
  }

  setSaving(true);
  setError('');
  setSuccess('');

  try {
    if (section.isTypeDesc) {
      await resetTypeSettings(
        course.id,
        section.typeId
      );

      setTypeDescriptions(prev => ({
        ...prev,
        [section.typeId]: ''
      }));

      setTypeCooldownOverrides(
        prev => ({
          ...prev,
          [section.typeId]:
            null
        })
      );

      setTypeQuizOverrides(
        prev => ({
          ...prev,
          [section.typeId]:
            null
        })
      );

      setEditValues(prev => ({
        ...prev,
        [section.id]:
          section.defaultContent ||
          ''
      }));

      setAssignmentEdits(prev => ({
        ...prev,
        [section.id]: {
          descriptionIsOverride:
            false,

          cooldownIsOverride:
            false,

          cooldownMinutes:
            section.defaultCooldownMinutes,

          quizIsOverride:
            false,

          quizQuestions:
            makeQuizDraft(
              section.defaultQuizQuestions
            )
        }
      }));
    } else {
      await deleteCourseContent(
        course.id,
        section.id
      );

      await updateCourseSectionOverrides(
        section._cssId,
        null,
        null
      );

      setCourseContent(prev => {
        const next = {
          ...prev
        };

        delete next[
          section.id
        ];

        delete next[
          `${section.id}_with`
        ];

        delete next[
          `${section.id}_without`
        ];

        return next;
      });

      setCourseSections(prev =>
        prev.map(row =>
          row.id === section._cssId
            ? {
                ...row,
                cooldown_minutes_override:
                  null,
                quiz_questions_override:
                  null
              }
            : row
        )
      );

      if (
        section
          .has_clarificatory_variants
      ) {
        setEditValues(prev => ({
          ...prev,

          [`${section.id}_with`]:
            section
              .content_with_clarificatory ||
            '',

          [`${section.id}_without`]:
            section
              .content_without_clarificatory ||
            ''
        }));
      } else {
        setEditValues(prev => ({
          ...prev,
          [section.id]:
            section.content ||
            ''
        }));
      }

      setSectionEdits(prev => ({
        ...prev,
        [section.id]: {
          contentIsOverride:
            false,

          cooldownIsOverride:
            false,

          cooldownMinutes:
            section.defaultCooldownMinutes,

          quizIsOverride:
            false,

          quizQuestions:
            makeQuizDraft(
              section.defaultQuizQuestions
            )
        }
      }));
    }

    setSuccess(
      `Reset to ${sourceName} defaults.`
    );
  } catch (err) {
    console.error(
      'Failed to reset syllabus section:',
      err
    );

    setError(
      err?.message ||
        'Failed to reset section.'
    );
  } finally {
    setSaving(false);
  }
}
  async function handleSave(
  section
) {
  setSaving(true);
  setError('');
  setSuccess('');

  try {
    if (section.isTypeDesc) {
      const draft =
        assignmentEdits[
          section.id
        ] || {};

      let cooldownOverride =
        null;

      if (
        draft.cooldownIsOverride
      ) {
        cooldownOverride =
          Number(
            draft.cooldownMinutes
          );

        if (
          !Number.isFinite(
            cooldownOverride
          ) ||
          cooldownOverride < 0
        ) {
          throw new Error(
            'Cooldown must be 0 or greater.'
          );
        }
      }

      const quizOverride =
        draft.quizIsOverride
          ? serializeQuizDraft(
              draft.quizQuestions ||
              []
            )
          : null;

      const description =
        draft.descriptionIsOverride
          ? (
              editValues[
                section.id
              ] || ''
            )
          : '';

      await upsertTypeSettings(
        course.id,
        section.typeId,
        {
          description,
          syllabusCooldownMinutes:
            cooldownOverride,
          syllabusQuizQuestions:
            quizOverride
        }
      );

      setTypeDescriptions(prev => ({
        ...prev,
        [section.typeId]:
          description
      }));

      setTypeCooldownOverrides(
        prev => ({
          ...prev,
          [section.typeId]:
            cooldownOverride
        })
      );

      setTypeQuizOverrides(
        prev => ({
          ...prev,
          [section.typeId]:
            quizOverride
        })
      );
    } else {
      const draft =
        sectionEdits[
          section.id
        ] || {};

      if (
        section
          .has_clarificatory_variants
      ) {
        await Promise.all([
          upsertCourseContent(
            course.id,
            section.id,
            editValues[
              `${section.id}_with`
            ] || ''
          ),

          upsertCourseContent(
            course.id,
            section.id,
            editValues[
              `${section.id}_without`
            ] || ''
          )
        ]);

        setCourseContent(prev => ({
          ...prev,

          [`${section.id}_with`]:
            editValues[
              `${section.id}_with`
            ] || '',

          [`${section.id}_without`]:
            editValues[
              `${section.id}_without`
            ] || ''
        }));
      } else if (
        draft.contentIsOverride
      ) {
        await upsertCourseContent(
          course.id,
          section.id,
          editValues[
            section.id
          ] || ''
        );

        setCourseContent(prev => ({
          ...prev,

          [section.id]:
            editValues[
              section.id
            ] || ''
        }));
      }

      let cooldownOverride =
        null;

      if (
        draft.cooldownIsOverride
      ) {
        cooldownOverride =
          Number(
            draft.cooldownMinutes
          );

        if (
          !Number.isFinite(
            cooldownOverride
          ) ||
          cooldownOverride < 0
        ) {
          throw new Error(
            'Cooldown must be 0 or greater.'
          );
        }
      }

      const quizOverride =
        draft.quizIsOverride
          ? serializeQuizDraft(
              draft.quizQuestions ||
              []
            )
          : null;

      await updateCourseSectionOverrides(
        section._cssId,
        cooldownOverride,
        quizOverride
      );

      setCourseSections(prev =>
        prev.map(row =>
          row.id === section._cssId
            ? {
                ...row,
                cooldown_minutes_override:
                  cooldownOverride,
                quiz_questions_override:
                  quizOverride
              }
            : row
        )
      );
    }

    setSuccess('Saved.');
  } catch (err) {
    console.error(
      'Failed to save syllabus:',
      err
    );

    setError(
      err?.message ||
        'Failed to save.'
    );
  } finally {
    setSaving(false);
  }
}

  async function handleMove(
    section,
    direction
  ) {
    const visibleEntries =
      getDisplaySections();

    const idx =
      visibleEntries.findIndex(
        entry =>
          entry._orderKey ===
          section._orderKey
      );

    if (idx < 0) return;

    const swapIdx =
      idx + direction;

    if (
      swapIdx < 0 ||
      swapIdx >=
        visibleEntries.length
    ) {
      return;
    }

    const current =
      visibleEntries[idx];

    const swap =
      visibleEntries[swapIdx];

    const allEntries =
      getOrderableEntries();

    const currentAllIdx =
      allEntries.findIndex(
        entry =>
          entry._orderKey ===
          current._orderKey
      );

    const swapAllIdx =
      allEntries.findIndex(
        entry =>
          entry._orderKey ===
          swap._orderKey
      );

    if (
      currentAllIdx < 0 ||
      swapAllIdx < 0
    ) {
      return;
    }

    const reordered = [
      ...allEntries
    ];

    [
      reordered[currentAllIdx],
      reordered[swapAllIdx]
    ] = [
      reordered[swapAllIdx],
      reordered[currentAllIdx]
    ];

    const normalized =
      reordered.map(
        (entry, index) => ({
          ...entry,
          section_order:
            (index + 1) * 10
        })
      );

    const sectionOrders = {};
    const assignmentOrders = {};

    for (const entry of normalized) {
      if (
        entry._orderKind ===
        'section'
      ) {
        sectionOrders[
          entry._cssId
        ] = entry.section_order;
      } else if (
        entry._orderKind ===
        'type'
      ) {
        assignmentOrders[
          entry.typeId
        ] = entry.section_order;
      }
    }

    setCourseSections(prev =>
      prev
        .map(row => (
          Object.prototype.hasOwnProperty.call(
            sectionOrders,
            row.id
          )
            ? {
                ...row,
                section_order:
                  sectionOrders[row.id]
              }
            : row
        ))
        .sort(
          (a, b) =>
            Number(
              a.section_order || 0
            ) -
            Number(
              b.section_order || 0
            )
        )
    );

    setTypeOrders(prev => ({
      ...prev,
      ...assignmentOrders
    }));

    try {
      await Promise.all(
        normalized.map(entry => {
          if (
            entry._orderKind ===
            'section'
          ) {
            return updateSectionOrder(
              entry._cssId,
              entry.section_order
            );
          }

          return upsertTypeOrder(
            course.id,
            entry.typeId,
            typeDescriptions[
              entry.typeId
            ] ?? '',
            entry.section_order
          );
        })
      );
    } catch (err) {
      console.error(
        'Failed to reorder syllabus:',
        err
      );

      setError(
        'Failed to change syllabus order.'
      );

      await load();
    }
  }

  async function handleRemove(
  cs
) {
  const section =
    cs.syllabus_sections;

  const name =
    section?.name ||
    'this section';

  if (
    !window.confirm(
      `Remove "${name}" from this course's syllabus?`
    )
  ) {
    return;
  }

  setSaving(true);
  setError('');
  setSuccess('');

  try {
    await removeSectionFromCourse(
      cs.id
    );

    // Re-read the actual database state.
    // Do not merely hide the section locally.
    await load();

    setSuccess(
      `"${name}" removed.`
    );
  } catch (err) {
    console.error(
      'Failed to remove syllabus section:',
      err
    );

    setError(
      err?.message ||
      'Failed to remove syllabus section.'
    );
  } finally {
    setSaving(false);
  }
}

async function handleAdd(
  sectionId
) {
  const maxOrder =
    getOrderableEntries().reduce(
      (max, entry) =>
        Math.max(
          max,
          Number(entry.section_order) || 0
        ),
      0
    );

  await addSectionToCourse(
    course.id,
    sectionId,
    maxOrder + 10
  );

  await load();

  setShowAddPanel(false);

  setSuccess(
    'Section added.'
  );
}

async function handleAddAssignmentSection(
  assignment
) {
  const typeId =
    assignment.assignment_type_id;

  const maxOrder =
    getOrderableEntries().reduce(
      (max, entry) =>
        Math.max(
          max,
          Number(entry.section_order) || 0
        ),
      0
    );

  await upsertTypeOrder(
    course.id,
    typeId,
    '',
    maxOrder + 10
  );

  await load();

  setShowAddPanel(false);

  setSuccess(
    'Assignment section added.'
  );
}

async function handleRemoveAssignmentSection(
  section
) {
  if (
    !window.confirm(
      `Remove "${section.name}" from this course's syllabus?`
    )
  ) {
    return;
  }

  await removeTypeDescription(
    course.id,
    section.typeId
  );

  setTypeDescriptions(prev => {
    const next = { ...prev };
    delete next[section.typeId];
    return next;
  });

  setTypeOrders(prev => {
    const next = { ...prev };
    delete next[section.typeId];
    return next;
  });

  setSuccess(
    `"${section.name}" removed.`
  );
}

  async function handleCreateSection() {
    if (
      !newSectionName.trim()
    ) {
      return;
    }

    setSaving(true);

    try {
      const maxLibraryOrder =
        librarySections.reduce(
          (max, section) => {
            const value =
              Number(section.section_order);

            return Number.isFinite(value)
              ? Math.max(max, value)
              : max;
          },
          0
        );

      const result =
        await createSection({
          name:
            newSectionName.trim(),
          section_order:
            maxLibraryOrder + 10,
          cooldown_minutes: 0,
          has_quiz: true,
          is_variable: true,
          is_teaching_schedule:
            false,
          is_ai_permissions:
            false,
          has_clarificatory_variants:
            false,
          instructor_id:
            instructorId
        });

      const created =
        result[0];

      if (created) {
        setLibrarySections(
          prev => [
            ...prev,
            created
          ]
        );

        await handleAdd(
          created.id
        );
      }

      setNewSectionName('');
      setShowNewSectionForm(
        false
      );
    } catch (err) {
      setError(
        'Failed to create section.'
      );
    } finally {
      setSaving(false);
    }
  }

  const displaySections =
    getDisplaySections();

  async function handlePrintSyllabus(sectionToPrint = null) {
    if (printing) return;

    setPrinting(true);
    setError('');

    try {
      const [
        weeksResult,
        holidaysResult,
        meetingsResult,
        readingsResult,
        eventsResult,
        assignmentSettingsResult,
        scheduleContextResult,
        aiResult,
        attendanceWeight,
        groupWeeksResult
      ] = await Promise.all([
        fetchPrintWeeks(course.id),
        fetchPrintHolidays(instructorId),
        fetchPrintMeetings(course.id),
        fetchPrintReadings(course.id),
        fetchPrintEvents(course.id),
        fetchPrintAssignmentTypeSettings(course.id),
        loadStudentAssignmentScheduleContext({
          courseIds: [course.id],
          enrolments: [],
          includeChoiceState: false
        }),
        fetchPrintAiPermissions(course.id),
        fetchPrintAttendanceWeight(course.id),
        course.group_based_attendance
          ? fetchPrintAttendanceGroupWeeks(course.id)
          : Promise.resolve([])
      ]);

      const weeks = Array.isArray(weeksResult) ? weeksResult : [];
      const holidays = Array.isArray(holidaysResult) ? holidaysResult : [];
      const meetings = Array.isArray(meetingsResult) ? meetingsResult : [];
      const readings = Array.isArray(readingsResult) ? readingsResult : [];
      const events = Array.isArray(eventsResult) ? eventsResult : [];
      const aiPermissions = Array.isArray(aiResult) ? aiResult : [];
      const assignmentSettings =
        Array.isArray(
          assignmentSettingsResult
        )
          ? assignmentSettingsResult
          : [];

      const scheduleContext =
        scheduleContextResult || {
          scheduleItems: [],
          scheduleReadings: [],
          scheduleEvents: [],
          duplicateLinks: []
        };

      const printDisplayNames =
        new Map(
          assignmentSettings.map(
            setting => [
              String(
                setting
                  .assignment_type_id
              ),
              String(
                setting
                  .display_name ||
                ''
              ).trim()
            ]
          )
        );

      const namedPrintAssignments =
        courseAssignments.map(
          assignment => ({
            ...assignment,
            assignment_display_name:
              printDisplayNames.get(
                String(
                  assignment
                    .assignment_type_id
                )
              ) ||
              typeDisplayNames[
                assignment
                  .assignment_type_id
              ] ||
              assignment
                ?.assignment_types
                ?.name ||
              'Assignment'
          })
        );

      const printAssignments =
        expandInstructorAssignmentScheduleOccurrences({
          assignments:
            namedPrintAssignments,
          assignmentTypeSettings:
            assignmentSettings,
          scheduleContext
        });
      const groupWeeks = Array.isArray(groupWeeksResult) ? groupWeeksResult : [];

      const groupCounts = new Map();
      groupWeeks.forEach(row => {
        if (!row.attendance_group_id) return;
        groupCounts.set(
          row.attendance_group_id,
          (groupCounts.get(row.attendance_group_id) || 0) + 1
        );
      });

      const attendanceRequiredCount =
        course.group_based_attendance
          ? Math.max(0, ...groupCounts.values())
          : weeks.filter(week => week.attendance_enabled).length;

      const attendanceTableHtml =
        course.grading_mode === 'traditional' &&
        course.attendance_enabled !== false
          ? buildAttendanceContributionTableHtml({
              totalRequired: attendanceRequiredCount,
              weight: Number(attendanceWeight) || 0,
              ...normalizeAttendanceSettings(course)
            })
          : '';

      function getPrintContent(section) {
        if (section.isTypeDesc) {
          return (
            typeDescriptions[section.typeId] ||
            section.defaultContent ||
            ''
          );
        }

        if (section.is_teaching_schedule) return null;

        if (section.has_clarificatory_variants) {
          return course.cq_clarificatory_enabled
            ? (
                courseContent[`${section.id}_with`] ??
                section.content_with_clarificatory ??
                ''
              )
            : (
                courseContent[`${section.id}_without`] ??
                section.content_without_clarificatory ??
                ''
              );
        }

        return (
          courseContent[section.id] ??
          section.content ??
          ''
        );
      }

      function buildScheduleHtml() {
        function mondayAndSunday(week) {
          const classDate = new Date(week.class_date);
          const day = classDate.getDay();
          const monday = new Date(classDate);
          monday.setDate(
            classDate.getDate() -
            (day === 0 ? 6 : day - 1)
          );
          const sunday = new Date(monday);
          sunday.setDate(monday.getDate() + 6);
          return { monday, sunday };
        }

        return `
          <div class="print-schedule">
            ${weeks.map(week => {
              const { monday, sunday } = mondayAndSunday(week);

              const holiday = holidays.find(
                item => item.date === week.class_date
              );

              const weekMeetings = meetings.filter(item => {
                const date = new Date(item.meeting_date);
                return date >= monday && date <= sunday;
              });

              const dueAssignments =
                isFirstActualPrintClassOfWeek(
                  week,
                  weeks,
                  holidays
                )
                  ? printAssignments.filter(
                      assignment => {
                        if (
                          assignment
                            .assignment_types
                            ?.is_core
                        ) {
                          return false;
                        }

                        const requiresSubmission =
                          assignment
                            .assignment_types
                            ?.requires_submission ??
                          true;

                        const due =
                          requiresSubmission
                            ? (
                                assignment
                                  .effective_due_date ||
                                assignment
                                  .due_date
                              )
                            : (
                                assignment
                                  .occurs_on ||
                                assignment
                                  .effective_due_date ||
                                assignment
                                  .due_date
                              );

                        if (!due) {
                          return false;
                        }

                        return (
                          getPrintCalendarWeekStart(
                            due
                          ) ===
                          getPrintCalendarWeekStart(
                            week.class_date
                          )
                        );
                      }
                    )
                  : [];

              const weekReadings = readings
                .filter(item => item.course_week_id === week.id)
                .sort(
                  (a, b) =>
                    (Number(a.display_order) || 0) -
                    (Number(b.display_order) || 0)
                );

              const weekEvents = events
                .filter(
                  event =>
                    event.course_week_id ===
                    week.id
                )
                .sort(
                  (a, b) =>
                    String(
                      a.event_date || ''
                    ).localeCompare(
                      String(
                        b.event_date || ''
                      )
                    ) ||
                    String(
                      a.event_time || ''
                    ).localeCompare(
                      String(
                        b.event_time || ''
                      )
                    )
                );

              const description = Array.isArray(week.course_week_content)
                ? week.course_week_content[0]?.description
                : week.course_week_content?.description;

              const items = [
                ...(holiday
                  ? [{
                      type: 'holiday',
                      date: week.class_date,
                      text: `No Class: ${holiday.reason || ''}`
                    }]
                  : []),
                ...weekMeetings.map(item => ({
                  type: 'meeting',
                  date: item.meeting_date,
                  text:
                    `${item.meeting_type || ''} Self-Evaluation Meetings: ` +
                    formatPrintDate(item.meeting_date)
                })),
                ...weekEvents.map(event => {
                  const details = [];

                  if (event.event_date) {
                    details.push(
                      formatPrintDate(
                        event.event_date
                      )
                    );
                  }

                  if (event.event_time) {
                    details.push(
                      String(
                        event.event_time
                      ).slice(0, 5)
                    );
                  }

                  if (event.location) {
                    details.push(
                      event.location
                    );
                  }

                  return {
                    type: 'event',
                    date:
                      event.event_date ||
                      week.class_date,
                    text:
                      `Event: ${event.title || 'Event'}` +
                      (
                        details.length
                          ? ` — ${details.join(' · ')}`
                          : ''
                      )
                  };
                }),
                ...dueAssignments.map(assignment => {
                  const requiresSubmission =
                    assignment.assignment_types?.requires_submission ??
                    true;

                  const due =
                    requiresSubmission
                      ? (
                          assignment
                            .effective_due_date ||
                          assignment
                            .due_date
                        )
                      : (
                          assignment
                            .occurs_on ||
                          assignment
                            .effective_due_date ||
                          assignment
                            .due_date
                        );

                  const label =
                    (
                      assignment
                        .assignment_display_name ||
                      typeDisplayNames[
                        assignment
                          .assignment_type_id
                      ] ||
                      assignment
                        .assignment_types
                        ?.name ||
                      'Assignment'
                    ) +
                    (
                      assignment
                        .schedule_source_title
                        ? ` — ${assignment.schedule_source_title}`
                        : ''
                    );

                  return {
                    type: 'assignment',
                    date: due,
                    text: requiresSubmission
                      ? `${label} due ${formatPrintDate(due)}`
                      : label
                  };
                })
              ].sort(
                (a, b) =>
                  new Date(a.date) -
                  new Date(b.date)
              );

              return `
                <div class="print-schedule-row">
                  <div class="print-schedule-week">
                    <strong>W${escapePrintHtml(week.week_number)}</strong>
                    <div>${escapePrintHtml(formatPrintDate(week.class_date))}</div>
                  </div>

                  <div class="print-schedule-body">
                    ${items.map(item => `
                      <div class="print-schedule-item print-${item.type}">
                        ${escapePrintHtml(item.text)}
                      </div>
                    `).join('')}

                    ${description
                      ? `<div class="print-schedule-description">${escapePrintHtml(stripPrintHtml(description))}</div>`
                      : ''}

                    ${weekReadings.map(item => {
                      const reading = item.course_readings;
                      if (!reading) return '';
                      const author = reading.author ? ` by ${reading.author}` : '';
                      return `
                        <div class="print-schedule-reading">
                          <strong>Reading:</strong>
                          ${escapePrintHtml((reading.title || 'Reading') + author)}
                        </div>
                      `;
                    }).join('')}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        `;
      }

      function buildAiHtml() {
        const render = (rows, title, fallback) => {
          if (!rows.length) return '';
          return `
            <h3>${title}</h3>
            ${rows.map(item => `
              <div class="print-ai-permission">
                <strong>
                  ${escapePrintHtml(item.ai_use_types?.label || fallback)}
                </strong>
                ${item.ai_use_types?.description
                  ? `<div>${escapePrintHtml(item.ai_use_types.description)}</div>`
                  : ''}
                ${item.note
                  ? `<div class="print-note">Course note: ${escapePrintHtml(item.note)}</div>`
                  : ''}
              </div>
            `).join('')}
          `;
        };

        const permitted = aiPermissions.filter(item => item.permitted);
        const notPermitted = aiPermissions.filter(item => !item.permitted);

        return `
          <div class="print-ai-permissions">
            ${permitted.length
              ? render(permitted, 'Permitted', 'Permitted AI use')
              : '<div>No AI uses are currently permitted.</div>'}
            ${render(notPermitted, 'Not Permitted', 'AI use')}
          </div>
        `;
      }

      const sectionsToPrint =
        sectionToPrint
          ? [sectionToPrint]
          : displaySections;

      const root = document.createElement('div');
      root.setAttribute('data-syllabus-print-root', 'true');

      Object.assign(root.style, {
        display: 'block',
        width: '760px',
        padding: '34px 42px',
        background: '#ffffff',
        color: '#243b53',
        fontFamily: 'Arial, sans-serif',
        fontSize: '14px',
        lineHeight: '1.55',
        boxSizing: 'border-box'
      });

      const courseName =
        course.name ||
        course.course_titles?.name ||
        'Course';

      root.innerHTML = `
        <style>
          [data-syllabus-print-root] h1 {
            font-size: 25px;
            margin: 0 0 8px;
          }

          [data-syllabus-print-root] .print-subtitle {
            color: #627d98;
            margin-bottom: 26px;
            font-size: 13px;
          }

          [data-syllabus-print-root] .print-section {
            margin: 0 0 30px;
          }

          [data-syllabus-print-root] .print-section h2 {
            font-size: 19px;
            margin: 0 0 12px;
            padding-bottom: 6px;
            border-bottom: 1px solid #d9e2ec;
          }

          [data-syllabus-print-root] .print-rich-text p {
            margin: 0 0 10px;
          }

          [data-syllabus-print-root] .print-rich-text ul,
          [data-syllabus-print-root] .print-rich-text ol {
            padding-left: 24px;
          }

          [data-syllabus-print-root] .print-schedule-row {
            display: grid;
            grid-template-columns: 118px 1fr;
            gap: 14px;
            padding: 10px 0;
            border-bottom: 1px solid #e5eaee;
          }

          [data-syllabus-print-root] .print-schedule-week {
            color: #627d98;
            font-size: 12px;
          }

          [data-syllabus-print-root] .print-schedule-item {
            margin-bottom: 4px;
          }

          [data-syllabus-print-root] .print-holiday {
            color: #a12a2a;
          }

          [data-syllabus-print-root] .print-meeting {
            color: #5a4383;
          }

          [data-syllabus-print-root] .print-event {
            color: #7a5a20;
          }

          [data-syllabus-print-root] .print-assignment {
            color: #2f6f9f;
          }

          [data-syllabus-print-root] .print-schedule-description,
          [data-syllabus-print-root] .print-note {
            color: #627d98;
          }

          [data-syllabus-print-root] .print-schedule-reading {
            margin-top: 5px;
          }

          [data-syllabus-print-root] .print-ai-permission {
            margin: 0 0 10px;
          }

          [data-syllabus-print-root] img {
            max-width: 100% !important;
            height: auto !important;
          }

          [data-syllabus-print-root] table {
            width: 100% !important;
            border-collapse: collapse;
          }

          [data-syllabus-print-root] th,
          [data-syllabus-print-root] td {
            border: 1px solid #d9e2ec;
            padding: 6px;
            vertical-align: top;
          }
        </style>

        <h1>${escapePrintHtml(courseName)} Syllabus</h1>

        ${sectionToPrint
          ? `<div class="print-subtitle">${escapePrintHtml(sectionToPrint.name)}</div>`
          : ''}

        ${sectionsToPrint.map(section => {
          const content = getPrintContent(section);

          const body = section.is_teaching_schedule
            ? buildScheduleHtml()
            : `
                ${content
                  ? `<div class="print-rich-text">${substitutePrintVariables(
                      content,
                      course,
                      attendanceTableHtml
                    )}</div>`
                  : ''}

                ${section.is_ai_permissions === true || section.name === 'AI Permissions'
                  ? buildAiHtml()
                  : ''}
              `;

          return `
            <section class="print-section">
              <h2>${escapePrintHtml(section.name)}</h2>
              ${body}
            </section>
          `;
        }).join('')}
      `;

      const suffix = sectionToPrint
        ? ` - ${safePrintFilePart(sectionToPrint.name)}`
        : '';

      await downloadSyllabusPdfRoot(
        root,
        `${safePrintFilePart(courseName)} Syllabus${suffix}.pdf`
      );

    } catch (err) {
      console.error(
        'Instructor syllabus PDF generation failed:',
        err
      );

      setError(
        err?.message ||
        'Could not generate the syllabus PDF.'
      );
    } finally {
      setPrinting(false);
    }
  }

  if (loading) {
    return (
      <div style={styles.muted}>
        Loading...
      </div>
    );
  }

  return (
    <div>
      <div style={styles.syllabusHeader}>
        <h2 style={styles.subheading}>
          Syllabus
        </h2>

        <button
          type="button"
          style={styles.printAllBtn}
          disabled={printing}
          onClick={() => handlePrintSyllabus()}
        >
          {printing
            ? 'Preparing PDF...'
            : 'Print All'}
        </button>
      </div>

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      {success && (
        <div style={styles.success}>
          {success}
        </div>
      )}

      <div style={styles.list}>
  {displaySections.map(
  (section, index) => {
    const isExpanded =
      expandedId === section.id;

    const isVariable =
      section.is_variable ||
      section.isTypeDesc;

    const isClarificatory =
      section
        .has_clarificatory_variants;

    const assignmentEdit =
      section.isTypeDesc
        ? (
            assignmentEdits[
              section.id
            ] || {}
          )
        : null;

    const sectionEdit =
      !section.isTypeDesc
        ? (
            sectionEdits[
              section.id
            ] || {}
          )
        : null;

    const cs =
      courseSections.find(
        c =>
          c.syllabus_sections
            ?.id ===
          section.id
      );

    const isMovable =
  !!section._orderKey;

const isRemovable =
  section.isTypeDesc ||
  !!cs;

    const displayIndex =
      displaySections.findIndex(
        item =>
          item._orderKey ===
          section._orderKey
      );

    return (
      <div
        key={section.id}
        style={
          styles.sectionItem
        }
      >
        <div
          style={
            styles.sectionHeader
          }
        >
          <button
            style={
              styles.sectionBtn
            }
            onClick={() =>
              handleExpand(
                section
              )
            }
          >
            <div
              style={
                styles
                  .sectionBtnLeft
              }
            >
              <span
                style={
                  styles
                    .sectionOrder
                }
              >
                {index + 1}
              </span>

              <span
                style={
                  styles
                    .sectionName
                }
              >
                {section.name}
              </span>

              <div
                style={
                  styles.pills
                }
              >
                {isVariable && (
                  <span
                    style={
                      styles.pill
                    }
                  >
                    Editable here
                  </span>
                )}

                {!isVariable &&
                  !section.isTypeDesc && (
                    <span
                      style={
                        styles
                          .pillGrey
                      }
                    >
                      Global default
                    </span>
                  )}

                {isClarificatory && (
                  <span
                    style={
                      styles
                        .pillAlt
                    }
                  >
                    Two versions
                  </span>
                )}
              </div>
            </div>

            <span
              style={
                styles.chevron
              }
            >
              {isExpanded
                ? '▾'
                : '▸'}
            </span>
          </button>

          <button
            type="button"
            style={styles.printLink}
            disabled={printing}
            onClick={event => {
              event.stopPropagation();
              handlePrintSyllabus(section);
            }}
          >
            Print
          </button>

          {(isMovable ||
            isRemovable) && (
            <div
              style={
                styles.moveButtons
              }
            >
              {isMovable && (
                <>
                  <button
                    style={
                      styles.moveBtn
                    }
                    onClick={() =>
                      handleMove(
                        section,
                        -1
                      )
                    }
                    disabled={
                      displayIndex <= 0
                    }
                    title="Move up"
                  >
                    ↑
                  </button>

                  <button
                    style={
                      styles.moveBtn
                    }
                    onClick={() =>
                      handleMove(
                        section,
                        1
                      )
                    }
                    disabled={
                      displayIndex < 0 ||
                      displayIndex >=
                        displaySections.length -
                          1
                    }
                    title="Move down"
                  >
                    ↓
                  </button>
                </>
              )}

              {isRemovable && (
                <button
                  style={
                    styles.removeBtn
                  }
                  onClick={() =>
  section.isTypeDesc
    ? handleRemoveAssignmentSection(
        section
      )
    : handleRemove(
        cs
      )
}
                  title="Remove from course"
                >
                  ✕
                </button>
              )}
            </div>
          )}
        </div>

        {isExpanded && (
          <div
            style={
              styles.sectionContent
            }
          >
            {section
              .is_teaching_schedule ? (
              <div
                style={
                  styles.readOnly
                }
              >
                <p
                  style={
                    styles.muted
                  }
                >
                  This section is
                  always included.
                  Its contents are
                  generated
                  automatically from
                  the course&apos;s
                  Teaching Schedule.
                  Use the arrows above
                  to change its
                  syllabus position.
                </p>
              </div>
            ) : section.isTypeDesc ? (
              <>
                <p
                  style={
                    styles.muted
                  }
                >
                  Course-specific
                  settings for{' '}
                  <strong>
                    {section.name}
                  </strong>
                  . Changes here apply
                  only to this course.
                </p>

                <VariablePicker
                  onInsert={p => {
                    setEditValues(
                      prev => ({
                        ...prev,

                        [section.id]:
                          (
                            prev[
                              section
                                .id
                            ] || ''
                          ) + p
                      })
                    );

                    setAssignmentEdits(
                      prev => ({
                        ...prev,

                        [section.id]: {
                          ...prev[
                            section
                              .id
                          ],

                          descriptionIsOverride:
                            true
                        }
                      })
                    );
                  }}
                />

                <RichTextEditor
                  value={
                    editValues[
                      section.id
                    ] || ''
                  }
                  onChange={val => {
                    setEditValues(
                      prev => ({
                        ...prev,

                        [section.id]:
                          val
                      })
                    );

                    setAssignmentEdits(
                      prev => ({
                        ...prev,

                        [section.id]: {
                          ...prev[
                            section
                              .id
                          ],

                          descriptionIsOverride:
                            true
                        }
                      })
                    );
                  }}
                />

                <CourseOverrideSettings
                  sourceLabel="Assignment Library"
                  draft={
                    assignmentEdit
                  }
                  onCooldownChange={
                    value =>
                      setAssignmentEdits(
                        prev => ({
                          ...prev,

                          [section.id]: {
                            ...prev[
                              section
                                .id
                            ],

                            cooldownIsOverride:
                              true,

                            cooldownMinutes:
                              value
                          }
                        })
                      )
                  }
                  onQuizQuestionChange={(
                    questionIndex,
                    patch
                  ) =>
                    setAssignmentEdits(
                      prev => {
                        const current =
                          prev[
                            section.id
                          ] || {};

                        const questions =
                          [
                            ...(
                              current
                                .quizQuestions ||
                              []
                            )
                          ];

                        questions[
                          questionIndex
                        ] = {
                          ...questions[
                            questionIndex
                          ],
                          ...patch
                        };

                        return {
                          ...prev,

                          [section.id]: {
                            ...current,

                            quizIsOverride:
                              true,

                            quizQuestions:
                              questions
                          }
                        };
                      }
                    )
                  }
                  onAddQuizQuestion={() =>
                    setAssignmentEdits(
                      prev => {
                        const current =
                          prev[
                            section.id
                          ] || {};

                        return {
                          ...prev,

                          [section.id]: {
                            ...current,

                            quizIsOverride:
                              true,

                            quizQuestions: [
                              ...(
                                current
                                  .quizQuestions ||
                                []
                              ),

                              {
                                question:
                                  '',

                                choicesText:
                                  '',

                                correct_answer_index:
                                  ''
                              }
                            ]
                          }
                        };
                      }
                    )
                  }
                  onRemoveQuizQuestion={
                    questionIndex =>
                      setAssignmentEdits(
                        prev => {
                          const current =
                            prev[
                              section.id
                            ] || {};

                          return {
                            ...prev,

                            [section.id]: {
                              ...current,

                              quizIsOverride:
                                true,

                              quizQuestions:
                                (
                                  current
                                    .quizQuestions ||
                                  []
                                ).filter(
                                  (
                                    _,
                                    qIndex
                                  ) =>
                                    qIndex !==
                                    questionIndex
                                )
                            }
                          };
                        }
                      )
                  }
                />

                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginTop: '14px',
                    flexWrap: 'wrap'
                  }}
                >
                  <button
                    type="button"
                    style={
                      styles.secondaryBtn
                    }
                    disabled={saving}
                    onClick={() =>
                      handleResetToDefault(
                        section
                      )
                    }
                  >
                    Reset to Assignment Library
                  </button>

                  <button
                    style={
                      styles.btn
                    }
                    disabled={saving}
                    onClick={() =>
                      handleSave(
                        section
                      )
                    }
                  >
                    {saving
                      ? 'Saving...'
                      : 'Save'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p
                  style={
                    styles.muted
                  }
                >
                  Changes here apply
                  only to this course
                  and do not change
                  the General
                  Syllabus version.
                </p>

                {isClarificatory ? (
                  <>
                    <div
                      style={
                        styles
                          .variantLabel
                      }
                    >
                      Version when
                      clarificatory
                      questions are
                      enabled:
                    </div>

                    <VariablePicker
                      onInsert={p =>
                        setEditValues(
                          prev => ({
                            ...prev,

                            [`${section.id}_with`]:
                              (
                                prev[
                                  `${section.id}_with`
                                ] ||
                                ''
                              ) + p
                          })
                        )
                      }
                    />

                    <RichTextEditor
                      value={
                        editValues[
                          `${section.id}_with`
                        ] || ''
                      }
                      onChange={val =>
                        setEditValues(
                          prev => ({
                            ...prev,

                            [`${section.id}_with`]:
                              val
                          })
                        )
                      }
                    />

                    <div
                      style={{
                        ...styles
                          .variantLabel,

                        marginTop:
                          '16px'
                      }}
                    >
                      Version when
                      clarificatory
                      questions are
                      not enabled:
                    </div>

                    <VariablePicker
                      onInsert={p =>
                        setEditValues(
                          prev => ({
                            ...prev,

                            [`${section.id}_without`]:
                              (
                                prev[
                                  `${section.id}_without`
                                ] ||
                                ''
                              ) + p
                          })
                        )
                      }
                    />

                    <RichTextEditor
                      value={
                        editValues[
                          `${section.id}_without`
                        ] || ''
                      }
                      onChange={val =>
                        setEditValues(
                          prev => ({
                            ...prev,

                            [`${section.id}_without`]:
                              val
                          })
                        )
                      }
                    />
                  </>
                ) : (
                  <>
                    <VariablePicker
                      onInsert={p => {
                        setEditValues(
                          prev => ({
                            ...prev,

                            [section.id]:
                              (
                                prev[
                                  section.id
                                ] ||
                                ''
                              ) + p
                          })
                        );

                        setSectionEdits(
                          prev => ({
                            ...prev,
                            [section.id]: {
                              ...prev[
                                section.id
                              ],
                              contentIsOverride:
                                true
                            }
                          })
                        );
                      }}
                    />

                    <RichTextEditor
                      value={
                        editValues[
                          section.id
                        ] || ''
                      }
                      onChange={val => {
                        setEditValues(
                          prev => ({
                            ...prev,

                            [section.id]:
                              val
                          })
                        );

                        setSectionEdits(
                          prev => ({
                            ...prev,
                            [section.id]: {
                              ...prev[
                                section.id
                              ],
                              contentIsOverride:
                                true
                            }
                          })
                        );
                      }}
                    />
                  </>
                )}

                <CourseOverrideSettings
                  sourceLabel="General Syllabus"
                  draft={
                    sectionEdit
                  }
                  onCooldownChange={
                    value =>
                      setSectionEdits(
                        prev => ({
                          ...prev,

                          [section.id]: {
                            ...prev[
                              section.id
                            ],

                            cooldownIsOverride:
                              true,

                            cooldownMinutes:
                              value
                          }
                        })
                      )
                  }
                  onQuizQuestionChange={(
                    questionIndex,
                    patch
                  ) =>
                    setSectionEdits(
                      prev => {
                        const current =
                          prev[
                            section.id
                          ] || {};

                        const questions =
                          [
                            ...(
                              current
                                .quizQuestions ||
                              []
                            )
                          ];

                        questions[
                          questionIndex
                        ] = {
                          ...questions[
                            questionIndex
                          ],
                          ...patch
                        };

                        return {
                          ...prev,

                          [section.id]: {
                            ...current,

                            quizIsOverride:
                              true,

                            quizQuestions:
                              questions
                          }
                        };
                      }
                    )
                  }
                  onAddQuizQuestion={() =>
                    setSectionEdits(
                      prev => {
                        const current =
                          prev[
                            section.id
                          ] || {};

                        return {
                          ...prev,

                          [section.id]: {
                            ...current,

                            quizIsOverride:
                              true,

                            quizQuestions: [
                              ...(
                                current
                                  .quizQuestions ||
                                []
                              ),

                              {
                                question:
                                  '',

                                choicesText:
                                  '',

                                correct_answer_index:
                                  ''
                              }
                            ]
                          }
                        };
                      }
                    )
                  }
                  onRemoveQuizQuestion={
                    questionIndex =>
                      setSectionEdits(
                        prev => {
                          const current =
                            prev[
                              section.id
                            ] || {};

                          return {
                            ...prev,

                            [section.id]: {
                              ...current,

                              quizIsOverride:
                                true,

                              quizQuestions:
                                (
                                  current
                                    .quizQuestions ||
                                  []
                                ).filter(
                                  (
                                    _,
                                    qIndex
                                  ) =>
                                    qIndex !==
                                    questionIndex
                                )
                            }
                          };
                        }
                      )
                  }
                />

                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginTop: '14px',
                    flexWrap: 'wrap'
                  }}
                >
                  <button
                    type="button"
                    style={
                      styles.secondaryBtn
                    }
                    disabled={saving}
                    onClick={() =>
                      handleResetToDefault(
                        section
                      )
                    }
                  >
                    Reset to General Syllabus
                  </button>

                  <button
                    style={
                      styles.btn
                    }
                    disabled={saving}
                    onClick={() =>
                      handleSave(
                        section
                      )
                    }
                  >
                    {saving
                      ? 'Saving...'
                      : 'Save'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
)}
</div>

      <div
        style={{
          marginTop: '16px'
        }}
      >
        <button
          style={
            styles.secondaryBtn
          }
          onClick={() =>
            setShowAddPanel(
              prev => !prev
            )
          }
        >
          {showAddPanel
            ? 'Cancel'
            : '+ Add Section'}
        </button>
      </div>

      {showAddPanel && (
        <div
          style={styles.addPanel}
        >
          <h3
            style={
              styles.addHeading
            }
          >
            Section Library
          </h3>

          {availableToAdd.length ===
  0 &&
  availableAssignmentTypes.length ===
    0 &&
  !showNewSectionForm && (
              <p
                style={
                  styles.muted
                }
              >
                All your sections are
                already in this course.
              </p>
            )}

          {availableToAdd.map(
            section => (
              <div
                key={section.id}
                style={
                  styles.libraryItem
                }
              >
                <span
                  style={
                    styles.libraryName
                  }
                >
                  {section.name}
                </span>

                <button
                  style={
                    styles.addBtn
                  }
                  onClick={() =>
                    handleAdd(
                      section.id
                    )
                  }
                >
                  Add
                </button>
              </div>
            )
          )}

{availableAssignmentTypes.length >
  0 && (
    <>
      <h3
        style={{
          ...styles.addHeading,
          marginTop: '16px'
        }}
      >
        Assignment Sections
      </h3>

      {availableAssignmentTypes.map(
        assignment => (
          <div
            key={
              assignment.assignment_type_id
            }
            style={
              styles.libraryItem
            }
          >
            <span
              style={
                styles.libraryName
              }
            >
              {
                typeDisplayNames[
                  assignment.assignment_type_id
                ] ||
                assignment.assignment_types
                  ?.name ||
                'Assignment'
              }
            </span>

            <button
              style={
                styles.addBtn
              }
              onClick={() =>
                handleAddAssignmentSection(
                  assignment
                )
              }
            >
              Add
            </button>
          </div>
        )
      )}
    </>
  )}

          <div
            style={{
              marginTop: '12px'
            }}
          >
            {!showNewSectionForm ? (
              <button
                style={
                  styles.secondaryBtn
                }
                onClick={() =>
                  setShowNewSectionForm(
                    true
                  )
                }
              >
                + Create New Section
              </button>
            ) : (
              <div
                style={
                  styles.newSectionForm
                }
              >
                <label
                  style={
                    styles.label
                  }
                >
                  Section name
                </label>

                <input
                  style={
                    styles.input
                  }
                  type="text"
                  value={
                    newSectionName
                  }
                  onChange={e =>
                    setNewSectionName(
                      e.target.value
                    )
                  }
                  placeholder="e.g. Attendance Policy"
                  autoFocus
                />

                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginTop:
                      '8px'
                  }}
                >
                  <button
                    style={
                      styles.btn
                    }
                    disabled={
                      saving ||
                      !newSectionName.trim()
                    }
                    onClick={
                      handleCreateSection
                    }
                  >
                    {saving
                      ? 'Creating...'
                      : 'Create & Add'}
                  </button>

                  <button
                    style={
                      styles.secondaryBtn
                    }
                    onClick={() => {
                      setShowNewSectionForm(
                        false
                      );

                      setNewSectionName(
                        ''
                      );
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  syllabusHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '12px'
  },

  subheading: {
    margin: 0,
    fontSize: '18px',
    color: '#1f2933'
  },

  printAllBtn: {
    border: '1px solid #b0cfe0',
    background: 'white',
    color: '#215b7d',
    borderRadius: '8px',
    padding: '7px 12px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    flexShrink: 0
  },

  printLink: {
    border: 'none',
    background: 'transparent',
    color: '#215b7d',
    padding: '0 10px',
    fontSize: '13px',
    fontWeight: '700',
    cursor: 'pointer',
    flexShrink: 0
  },

  muted: {
    color: '#66788a',
    fontSize: '14px',
    margin: '0 0 12px',
    lineHeight: '1.5'
  },

  error: {
    background: '#faeaea',
    border:
      '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },

  success: {
    background: '#e7f5ed',
    border:
      '1px solid #b9dfc9',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#216e45',
    fontSize: '14px',
    marginBottom: '16px'
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },

  sectionItem: {
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    overflow: 'hidden',
    background: 'white'
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'stretch'
  },

  sectionBtn: {
    flex: 1,
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    padding: '14px 16px',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left'
  },

  sectionBtnLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap'
  },

  sectionOrder: {
    background: '#e7f1f7',
    color: '#215b7d',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'center',
    fontSize: '12px',
    fontWeight: '700',
    flexShrink: 0
  },

  sectionName: {
    fontWeight: '700',
    fontSize: '14px',
    color: '#1f2933'
  },

  pills: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap'
  },

  pill: {
    background: '#e7f1f7',
    color: '#215b7d',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },

  pillGrey: {
    background: '#f0f4f7',
    color: '#66788a',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },

  pillAlt: {
    background: '#fff4d7',
    color: '#8a5b00',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700'
  },

  chevron: {
    color: '#66788a',
    fontSize: '16px',
    flexShrink: 0
  },

  moveButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '0 8px',
    borderLeft:
      '1px solid #f0f4f7'
  },

  moveBtn: {
    border:
      '1px solid #d7dee5',
    background: 'white',
    borderRadius: '4px',
    width: '26px',
    height: '26px',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#66788a',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'center'
  },

  removeBtn: {
    border:
      '1px solid #e8bcbc',
    background: 'white',
    borderRadius: '4px',
    width: '26px',
    height: '26px',
    cursor: 'pointer',
    fontSize: '12px',
    color: '#a12a2a',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'center',
    marginLeft: '4px'
  },

  sectionContent: {
    padding: '16px',
    borderTop:
      '1px solid #d7dee5'
  },

  variantLabel: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#1f2933',
    marginBottom: '8px'
  },

  variablePicker: {
    background: '#f7f9fa',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '10px 12px',
    marginBottom: '10px'
  },

  varLabel: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a',
    marginBottom: '6px',
    display: 'block'
  },

  varRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center'
  },

  varSelect: {
    flex: 1,
    border:
      '1px solid #d7dee5',
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '13px',
    background: 'white'
  },

  varInsertBtn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  btn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '9px 16px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  secondaryBtn: {
    border:
      '1px solid #d7dee5',
    background: 'white',
    color: '#1f2933',
    borderRadius: '8px',
    padding: '8px 14px',
    fontSize: '14px',
    cursor: 'pointer'
  },

  readOnly: {},

  previewContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#1f2933',
    background: '#f7f9fa',
    borderRadius: '8px',
    padding: '12px',
    border:
      '1px solid #d7dee5',
    maxHeight: '200px',
    overflow: 'auto'
  },

  addPanel: {
    marginTop: '12px',
    border:
      '1px solid #d7dee5',
    borderRadius: '10px',
    padding: '16px',
    background: '#f7f9fa'
  },

  addHeading: {
    margin: '0 0 12px',
    fontSize: '15px',
    color: '#1f2933',
    fontWeight: '700'
  },

  libraryItem: {
    display: 'flex',
    justifyContent:
      'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom:
      '1px solid #e7edf2'
  },

  libraryName: {
    fontSize: '14px',
    color: '#1f2933'
  },

  addBtn: {
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '5px 12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },

  newSectionForm: {
    padding: '12px',
    background: 'white',
    borderRadius: '8px',
    border:
      '1px solid #d7dee5'
  },

  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '6px',
    color: '#1f2933'
  },

  input: {
    width: '100%',
    border:
      '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '14px',
    boxSizing: 'border-box'
  }
};